"use client";

import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { AnimatePresence, m } from "motion/react";
import { Sparkles } from "lucide-react";
import { useTranslation } from "@/lib/i18n";
import { useSkipAnimations } from "@/lib/animations";
import { useConversation } from "@/lib/chat/conversation";
import AssistantPanel from "./AssistantPanel";
import {
  ASK_BAR_HEIGHT,
  BUBBLE_SIZE,
  DOCKED_WIDTH,
  EDGE_GAP,
  MAX_PANEL_VH,
  PAGE_MARGIN,
  TRAVEL_MS,
  UNFOLD_MS,
} from "./constants";
import { restingHeight, useNoticeHeight } from "./restingHeight";

interface Box {
  top: number;
  left: number;
  width: number;
}

/**
 * Mounts the one and only assistant.
 *
 * The panel is `position: fixed` in every appearance and never re-parents —
 * only its coordinates change. At rest they are the reservation slot's
 * viewport rectangle; once the slot scrolls out of view they become the
 * bottom-right corner. That is what lets the same DOM node travel instead of
 * one component fading into another.
 */
export default function AssistantHost({
  recordRetentionDays,
}: {
  /** Days a recorded conversation is kept, as the sweep enforces it. */
  recordRetentionDays: number;
}) {
  const { t } = useTranslation();
  const skipAnimations = useSkipAnimations();
  const {
    appearance,
    anchor,
    setAnchorVisible,
    open,
    close,
    isStreaming,
    messages,
  } = useConversation();

  const [box, setBox] = useState<Box | null>(null);
  /*
   * `w`/`h` are the layout viewport. `visibleH` and `keyboard` come from the
   * visual viewport: an on-screen keyboard shrinks only that on iOS and on
   * Android's default "resizes-visual", so `position: fixed` alone would leave
   * the composer behind it. `keyboard` is how much of the layout viewport's
   * bottom it covers. With no keyboard, `visibleH` is `h` and `keyboard` is 0,
   * which leaves every position below exactly as it was.
   */
  const [viewport, setViewport] = useState({
    w: 0,
    h: 0,
    visibleH: 0,
    keyboard: 0,
  });
  // Set once, the first time the visitor scrolls. See `measure`.
  const [auraSpent, setAuraSpent] = useState(false);
  const noticeHeight = useNoticeHeight();

  /*
   * Whether the coordinates should be TWEENED or simply followed.
   *
   * They change for two different reasons and want opposite treatment. An
   * appearance change — resting to docked — is the travel the design asks for,
   * and takes TRAVEL_MS. Scrolling while resting is not travel: the bar is
   * pinned to a slot in the page and has to sit exactly on it. Tweening that
   * re-aimed a 280ms ease at a new target on every frame, so the bar spent the
   * whole scroll chasing a position React had already written into its own
   * style attribute — two writers fighting over one property, which is what
   * made it lag behind the page.
   *
   * Adjusting state during render rather than in an effect is React's own
   * pattern for "a prop changed and some state derives from it"; an effect
   * would set the flag a frame late, which is exactly the frame that needs it.
   */
  const [previousAppearance, setPreviousAppearance] = useState(appearance);
  const [travelling, setTravelling] = useState(false);
  if (previousAppearance !== appearance) {
    setPreviousAppearance(appearance);
    setTravelling(true);
  }
  useEffect(() => {
    if (!travelling) return;
    const id = window.setTimeout(() => setTravelling(false), TRAVEL_MS);
    return () => window.clearTimeout(id);
  }, [travelling]);
  // Hover is tracked rather than expressed in CSS because the elevation is an
  // inline style that varies per appearance, and an inline style beats a class.
  const [hovered, setHovered] = useState(false);

  /*
   * Every setState here returns the previous object when nothing actually
   * moved. A scroll event that does not change the rectangle — and there are
   * many, since the browser fires them faster than it lays out — then costs a
   * comparison instead of a re-render of the whole panel.
   */
  const measure = useCallback(() => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const vv = window.visualViewport;
    const visibleH = vv ? Math.min(h, Math.round(vv.height)) : h;
    const keyboard = vv
      ? Math.max(0, Math.round(h - vv.offsetTop - vv.height))
      : 0;
    setViewport((v) =>
      v.w === w && v.h === h && v.visibleH === visibleH && v.keyboard === keyboard
        ? v
        : { w, h, visibleH, keyboard },
    );
    // The greeting is for someone who has just landed. The moment they scroll
    // it is spent, and stays spent: re-greeting them every time they return to
    // the top would be nagging rather than welcoming.
    if (window.scrollY > 8) setAuraSpent(true);
    if (!anchor) {
      setBox((b) => (b === null ? b : null));
      return;
    }
    const r = anchor.getBoundingClientRect();
    setBox((b) =>
      b && b.top === r.top && b.left === r.left && b.width === r.width
        ? b
        : { top: r.top, left: r.left, width: r.width },
    );
  }, [anchor]);

  useLayoutEffect(() => {
    /*
     * Scroll and resize are coalesced to one measurement per frame. Measuring
     * per event meant a forced layout — getBoundingClientRect flushes one — and
     * a render for every event the browser chose to fire, which on a trackpad
     * is well above one per frame.
     */
    let queued = 0;
    const onViewportChange = () => {
      if (queued) return;
      queued = window.requestAnimationFrame(() => {
        queued = 0;
        measure();
      });
    };
    // Measured after paint rather than synchronously in the effect body: the
    // rectangle is only meaningful once layout has settled, and a synchronous
    // setState here would cascade a second render on every mount.
    const raf = window.requestAnimationFrame(measure);
    // The keyboard opening or closing resizes only the visual viewport, and
    // fires no window resize on iOS.
    const vv = window.visualViewport;
    window.addEventListener("resize", onViewportChange);
    window.addEventListener("scroll", onViewportChange, { passive: true });
    vv?.addEventListener("resize", onViewportChange);
    vv?.addEventListener("scroll", onViewportChange);
    return () => {
      window.cancelAnimationFrame(raf);
      if (queued) window.cancelAnimationFrame(queued);
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("scroll", onViewportChange);
      vv?.removeEventListener("resize", onViewportChange);
      vv?.removeEventListener("scroll", onViewportChange);
    };
  }, [measure]);

  // Dock when the slot leaves the viewport, undock when it returns. Observing
  // the slot rather than comparing scrollY to a constant is self-correcting on
  // short viewports, and a route with no slot is simply always docked.
  useEffect(() => {
    if (!anchor) {
      setAnchorVisible(false);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => setAnchorVisible(entry.isIntersecting),
      { rootMargin: "-64px 0px 0px 0px", threshold: 0 }
    );
    io.observe(anchor);
    return () => io.disconnect();
  }, [anchor, setAnchorVisible]);

  // "/" focuses the assistant, as board 13 specifies. Guarded so it cannot
  // swallow a slash the visitor is typing into any other field.
  useEffect(() => {
    const onSlash = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement as HTMLElement | null;
      if (
        el &&
        (el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          el.isContentEditable)
      ) {
        return;
      }
      const input = document.querySelector<HTMLInputElement>(
        "[data-assistant-composer]"
      );
      if (!input) return;
      e.preventDefault();
      open();
      window.requestAnimationFrame(() => input.focus());
    };
    window.addEventListener("keydown", onSlash);
    return () => window.removeEventListener("keydown", onSlash);
  }, [open]);

  // Escape closes and returns focus to whatever opened it. Bound while open so
  // it cannot swallow Escape from an unrelated dialog.
  const isOpenAppearance =
    appearance === "inline-open" || appearance === "docked";
  useEffect(() => {
    if (!isOpenAppearance) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      close();
      // The bubble replaces the panel on the next paint; focus it when it lands.
      window.requestAnimationFrame(() => {
        document
          .querySelector<HTMLButtonElement>("[data-assistant-opener]")
          ?.focus();
      });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close, isOpenAppearance]);

  if (appearance === "hidden") return null;

  const isNarrow = viewport.w > 0 && viewport.w < DOCKED_WIDTH + EDGE_GAP * 2;
  // A share of what the visitor can see, so the panel shrinks with a keyboard.
  const maxHeight = viewport.h
    ? Math.round(viewport.visibleH * MAX_PANEL_VH)
    : 560;

  // Bubble: the only appearance that is not the panel.
  if (appearance === "collapsed") {
    return (
      <m.button
        type="button"
        onClick={open}
        aria-label={t("chat.bubble.label")}
        aria-expanded={false}
        data-assistant-opener
        initial={skipAnimations ? false : { scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileHover={skipAnimations ? undefined : { scale: 1.06 }}
        whileTap={skipAnimations ? undefined : { scale: 0.94 }}
        transition={{ duration: skipAnimations ? 0 : 0.18 }}
        /*
          Rings leave the bubble on a slow beat so a corner icon on a still page
          still reads as something live — and twice as fast while an answer is
          being written, which is the only way a visitor who closed the panel
          mid-answer can tell it is still coming. The rings are pseudo-elements
          on this button (see .assistant-halo in globals.css), so they cost no
          markup and no layout.
        */
        className={`fixed z-999 flex items-center justify-center rounded-full bg-mint text-black shadow-lg transition-colors hover:bg-mint-hover ${
          skipAnimations
            ? ""
            : `assistant-halo ${isStreaming ? "assistant-halo-busy" : ""}`
        }`}
        style={{
          right: EDGE_GAP,
          bottom: EDGE_GAP,
          width: BUBBLE_SIZE,
          height: BUBBLE_SIZE,
        }}
      >
        {/*
          The AI mark alone, at 22px — Penpot page "AI Assistant — Komponente",
          variant "D · Bubble", which holds exactly one `sparkles` icon on the
          mint disc. No speech bubble: the same mark carries the assistant in
          the composer chip and beside every answer, and the corner is where it
          has to be recognised fastest.
        */}
        <Sparkles
          size={22}
          aria-hidden="true"
          className={
            skipAnimations
              ? undefined
              : isStreaming
                ? "assistant-spark-busy"
                : "assistant-spark"
          }
        />
      </m.button>
    );
  }

  const isInline = appearance === "inline" || appearance === "inline-open";
  const isResting = appearance === "inline";

  /*
   * Before the slot has been measured — which includes the server render and
   * every paint up to hydration — the resting appearance is positioned by CSS
   * instead of by coordinates. It can be: the navigation is `position: fixed`,
   * so `main` starts at the document top and the slot's offset is just the
   * `pt-28 md:pt-32` on its wrapper, and it is centred at a known width. That
   * is what lets the Ask-Bar paint with the page rather than a second later.
   *
   * `100%` on a fixed element resolves against the viewport excluding the
   * scrollbar, which is the same box the centred slot is laid out in — so the
   * measured rectangle that replaces this lands on exactly the same pixels.
   *
   * The height is part of the measurement too: below the resting width it
   * depends on the notice, and the CSS path holds `--ask-bar-height` until
   * the notice has been measured, rather than falling back to ASK_BAR_HEIGHT
   * for a frame.
   */
  const unmeasuredResting = isResting && (!box || noticeHeight === null);
  const restHeight =
    box && noticeHeight !== null
      ? restingHeight(box.width, noticeHeight)
      : ASK_BAR_HEIGHT;

  // Target geometry. Inline sits over the reservation slot; docked sits in the
  // corner. Narrow viewports become a full-width sheet.
  let target: Box;
  if (isInline && box) {
    target = box;
  } else if (unmeasuredResting) {
    target = { top: 0, left: 0, width: 0 }; // unused; CSS positions it below
  } else if (isNarrow) {
    target = {
      top: Math.max(PAGE_MARGIN, viewport.h - maxHeight - EDGE_GAP),
      left: PAGE_MARGIN / 2,
      width: viewport.w - PAGE_MARGIN,
    };
  } else {
    target = {
      top: 0, // unused; bottom-anchored below
      left: viewport.w - DOCKED_WIDTH - EDGE_GAP,
      width: DOCKED_WIDTH,
    };
  }

  const dockedToBottom = !isInline;

  /*
   * Open over the slot, the panel's top is the slot's, so with a keyboard up it
   * must also stop short of the keyboard's top edge for the composer to stay in
   * view. Docked needs no cap: it is anchored to the keyboard's edge below.
   */
  const openHeight =
    appearance === "inline-open" && viewport.keyboard > 0
      ? Math.max(
          0,
          Math.min(
            maxHeight,
            viewport.h - viewport.keyboard - target.top - EDGE_GAP,
          ),
        )
      : maxHeight;

  /*
   * The resting Ask-Bar's greeting: a soft mint breath around the bar for the
   * first few seconds after the page paints, then it settles for good.
   *
   * It is a sibling rather than part of the panel because the panel clips its
   * own overflow and because its elevation is an inline style that changes on
   * hover — a CSS animation on the same property would fight it. Only for a
   * visitor who has not asked anything yet: once there is a conversation the
   * bar has already been found.
   */
  const showAura =
    isResting && !skipAnimations && !auraSpent && messages.length === 0;

  return (
    <>
      {showAura && (
        <div
          aria-hidden="true"
          className={`assistant-aura pointer-events-none fixed z-30 rounded-lg${
            unmeasuredResting ? " assistant-resting" : ""
          }`}
          style={{
            top: unmeasuredResting ? undefined : 0,
            left: unmeasuredResting ? undefined : target.left,
            width: unmeasuredResting ? undefined : target.width,
            height: unmeasuredResting ? "var(--ask-bar-height)" : restHeight,
            // Same transform as the panel, for the same reason.
            transform: unmeasuredResting
              ? undefined
              : `translate3d(0, ${target.top}px, 0)`,
          }}
        />
      )}

      {/* Scrim: only while open over the hero, and only below the navigation. */}
      <AnimatePresence>
        {appearance === "inline-open" && (
          <m.button
            type="button"
            aria-label={t("chat.close")}
            onClick={close}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: skipAnimations ? 0 : 0.18 }}
            className="fixed inset-x-0 bottom-0 z-997 cursor-default bg-white/62 dark:bg-black/60"
            style={{ top: 84 }}
          />
        )}
      </AnimatePresence>

      <m.div
        // One node, every appearance. Only coordinates animate — the element
        // never changes positioning scheme, which is what keeps the travel from
        // jumping.
        // Nothing to animate towards until the slot has been measured: the
        // first paint is the CSS-positioned resting bar, and animating from
        // placeholder coordinates would make it fly in from the corner.
        /*
         * The vertical position is a TRANSFORM, not `top`.
         *
         * `top` is a layout property: changing it every frame made the browser
         * lay out and repaint a 720px panel — with its 30px blurred shadow —
         * for the whole length of a scroll. `y` is composited, so following the
         * slot costs the compositor a matrix and nothing else. Nothing is lost
         * by the switch: the docked appearance is anchored by `bottom`, so the
         * vertical travel between the two was never tweenable to begin with —
         * motion cannot interpolate `top` to `undefined`.
         */
        animate={
          unmeasuredResting
            ? undefined
            : {
                y: dockedToBottom ? 0 : target.top,
                left: target.left,
                width: target.width,
                maxHeight: isResting ? restHeight : openHeight,
              }
        }
        transition={{
          duration: skipAnimations || !travelling ? 0 : TRAVEL_MS / 1000,
          ease: "easeOut",
          // The unfold is its own, quicker beat: the panel grows between header
          // and composer while the frame itself is not travelling.
          maxHeight: {
            duration: skipAnimations ? 0 : UNFOLD_MS / 1000,
            ease: "easeOut",
          },
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        /*
          The resting Ask-Bar passes UNDER the navigation, which is fixed at
          z-50: it is part of the page at rest, so scrolling it up behind the
          bar is what the eye expects — coming out on top of it read as a
          modal that had escaped. Every other appearance is over the page on
          purpose and stays above everything. z-40 still clears all page
          content, which tops out at z-20.
        */
        className={`fixed ${
          isResting ? "z-40" : "z-999"
        } flex flex-col overflow-hidden rounded-lg bg-assistant-surface${
          unmeasuredResting ? " assistant-resting" : ""
        }`}
        style={{
          // Zero, with the real offset carried by `y` above.
          top: unmeasuredResting ? undefined : dockedToBottom ? undefined : 0,
          // Above the keyboard, if one is up; see `viewport`.
          bottom: dockedToBottom ? EDGE_GAP + viewport.keyboard : undefined,
          left: unmeasuredResting ? undefined : target.left,
          width: unmeasuredResting ? undefined : target.width,
          // Always a number, never `none`: leaving it unset for one frame at the
          // CSS-to-measured handoff gave motion "none" as an animation origin,
          // which it cannot interpolate ("maxHeight from \"none\" to \"99\"").
          // The unmeasured value is a variable, not unset: the computed style
          // it resolves to is a length motion can start from.
          maxHeight: unmeasuredResting
            ? "var(--ask-bar-height)"
            : isResting
              ? restHeight
              : openHeight,
          // Elevation per appearance, taken from the boards rather than a
          // Tailwind preset: 14 "V4 · Gewählt" (rest), 11 (open), 12 (docked).
          // The ring is the first shadow layer rather than a second border, so
          // it costs no layout and cannot shift the reserved 99px.
          boxShadow: isResting
            ? hovered
              ? "0 0 0 3px var(--assistant-ring-strong), 0 2px 6px 0 var(--assistant-cast), 0 16px 40px 0 var(--assistant-glow-strong)"
              : "0 0 0 3px var(--assistant-ring), 0 2px 6px 0 var(--assistant-cast), 0 12px 30px 0 var(--assistant-glow)"
            : appearance === "inline-open"
              ? "0 0 0 3px var(--assistant-ring), 0 20px 48px 0 var(--assistant-cast-far), 0 2px 6px 0 var(--assistant-cast)"
              : "0 18px 44px 0 var(--assistant-cast-far), 0 2px 6px 0 var(--assistant-cast)",
          transition: "box-shadow 160ms ease-out, border-color 160ms ease-out",
          borderWidth: 1,
          borderStyle: "solid",
          // The mint edge is now the RESTING state, not just the open one: the
          // Ask-Bar has to read as askable before anyone touches it. Docked
          // keeps the neutral border — it sits on the page rather than over it,
          // and board 12 says so. Board 13 §02: hover raises the edge that is
          // already there; focus is the composer's own ring, in Composer.tsx.
          borderColor: dockedToBottom
            ? "var(--border-subtle)"
            : isResting && hovered
              ? "var(--assistant-edge-strong)"
              : "var(--assistant-edge)",
        }}
      >
        <AssistantPanel
          appearance={appearance}
          narrow={isNarrow}
          recordRetentionDays={recordRetentionDays}
        />
      </m.div>
    </>
  );
}
